import { memo, useState, useRef, useCallback } from "react";
import {
  IconSend,
  IconMicrophone,
  IconPaperclip,
  IconMoodSmile,
  IconX,
  IconPlayerStop,
  IconTrash,
} from "@tabler/icons-react";
import { useAudioRecorder } from "../../hooks";
import "./ChatInput.css";

/**
 * Input do chat com suporte a texto, emoji, anexos e áudio
 */
const ChatInput = memo(({
  onSendMessage,
  onSendAudio,
  onSendFile,
  disabled = false,
  isSending = false,
  placeholder = "Digite uma mensagem...",
  replyContext = null,
  onCancelReply,
}) => {
  // Combina disabled (não pode enviar) com isSending (envio em andamento).
  // isSending bloqueia clique-clique que cria mensagens duplicadas.
  const blocked = disabled || isSending;
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    isRecording,
    recordingTime,
    recordedAudio,
    audioBlob,
    startRecording,
    stopRecording,
    cancelRecording,
    clearRecordedAudio,
  } = useAudioRecorder();

  // Enviar mensagem. Guard contra envio duplicado: ignora chamadas
  // enquanto disabled ou isSending — protege de clique-clique e Enter
  // duplo em conexões lentas.
  const handleSend = useCallback(() => {
    if (blocked) return;
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !attachedFile) return;

    if (attachedFile) {
      onSendFile?.(attachedFile, trimmedMessage);
      setAttachedFile(null);
    } else {
      onSendMessage?.(trimmedMessage);
    }

    setMessage("");
    inputRef.current?.focus();
  }, [blocked, message, attachedFile, onSendMessage, onSendFile]);

  // Handler para tecla Enter
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Handler para arquivo
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
    }
    e.target.value = "";
  }, []);

  // Remover arquivo anexado
  const handleRemoveFile = useCallback(() => {
    setAttachedFile(null);
  }, []);

  // Iniciar gravação de áudio
  const handleStartRecording = useCallback(() => {
    startRecording();
  }, [startRecording]);

  // Parar gravação (mostra preview)
  const handleStopRecording = useCallback(async () => {
    await stopRecording();
    // Não envia automaticamente - mostra preview
  }, [stopRecording]);

  // Enviar áudio gravado
  const handleSendAudio = useCallback(() => {
    if (audioBlob && recordedAudio) {
      onSendAudio?.(audioBlob, recordedAudio, recordingTime);
      clearRecordedAudio();
    }
  }, [audioBlob, recordedAudio, recordingTime, onSendAudio, clearRecordedAudio]);

  // Cancelar/descartar áudio
  const handleCancelAudio = useCallback(() => {
    cancelRecording();
    clearRecordedAudio();
  }, [cancelRecording, clearRecordedAudio]);

  // Formatar tempo de gravação
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Adicionar emoji
  const handleEmojiSelect = useCallback((emoji) => {
    setMessage((prev) => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  }, []);

  // Emojis rápidos
  const quickEmojis = ["👍", "❤️", "😊", "😂", "🙏", "👏", "🔥", "✅"];

  // Modo de gravação ativa ou preview
  if (isRecording || recordedAudio) {
    return (
      <div className="sc-input">
        <div className="sc-input__main">
          {/* Botão cancelar/lixeira */}
          <button
            className="sc-input__btn sc-input__btn--danger"
            onClick={handleCancelAudio}
            title={isRecording ? "Cancelar gravação" : "Descartar áudio"}
          >
            <IconTrash size={20} />
          </button>

          {/* Área central - Timer ou Player */}
          <div className="sc-input__recording-area">
            {isRecording ? (
              <>
                <span className="sc-input__recording-dot" />
                <span className="sc-input__recording-time">
                  {formatRecordingTime(recordingTime)}
                </span>
              </>
            ) : (
              <audio
                controls
                preload="auto"
                className="sc-input__audio-player"
              >
                <source src={recordedAudio} type="audio/webm;codecs=opus" />
              </audio>
            )}
          </div>

          {/* Botão parar/enviar */}
          {isRecording ? (
            <button
              className="sc-input__send-btn"
              onClick={handleStopRecording}
              title="Parar gravação"
            >
              <IconPlayerStop size={24} />
            </button>
          ) : (
            <button
              className="sc-input__send-btn"
              onClick={handleSendAudio}
              title="Enviar áudio"
            >
              <IconSend size={24} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`sc-input ${blocked ? "sc-input--disabled" : ""}`}>
      {/* Banner: respondendo a comentário Instagram */}
      {replyContext && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#f3e8ff',
            borderLeft: '3px solid #7c3aed',
            padding: '8px 12px',
            margin: '0 12px 8px',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <span style={{ color: '#7c3aed', fontWeight: 600 }}>
            💬 Respondendo comentário no post
          </span>
          {replyContext.snippet && (
            <span style={{ color: '#64748b', fontStyle: 'italic' }}>
              "{replyContext.snippet}"
            </span>
          )}
          <button
            type="button"
            onClick={onCancelReply}
            aria-label="Cancelar resposta de comentário"
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#7c3aed',
              padding: 4,
            }}
          >
            <IconX size={14} />
          </button>
        </div>
      )}

      {/* Arquivo anexado */}
      {attachedFile && (
        <div className="sc-input__attachment">
          <div className="sc-input__attachment-info">
            <IconPaperclip size={16} />
            <span className="sc-input__attachment-name">
              {attachedFile.name}
            </span>
            <span className="sc-input__attachment-size">
              ({(attachedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button
            className="sc-input__attachment-remove"
            onClick={handleRemoveFile}
            disabled={isSending}
          >
            <IconX size={16} />
          </button>
        </div>
      )}

      <div className="sc-input__main">
        {/* Emoji picker toggle */}
        <div className="sc-input__emoji-container">
          <button
            className="sc-input__btn"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={blocked}
          >
            <IconMoodSmile size={24} />
          </button>

          {showEmojiPicker && (
            <div className="sc-input__emoji-picker">
              {quickEmojis.map((emoji) => (
                <button
                  key={emoji}
                  className="sc-input__emoji-btn"
                  onClick={() => handleEmojiSelect(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Anexar arquivo */}
        <button
          className="sc-input__btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={blocked}
        >
          <IconPaperclip size={24} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
        />

        {/* Campo de texto */}
        <div className="sc-input__field-wrapper">
          <textarea
            ref={inputRef}
            className="sc-input__field"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isSending ? "Enviando..." : placeholder}
            disabled={blocked}
            rows={1}
          />
        </div>

        {/* Botão enviar/gravar */}
        {message.trim() || attachedFile ? (
          <button
            className="sc-input__send-btn"
            onClick={handleSend}
            disabled={blocked}
            title={isSending ? "Enviando mensagem..." : "Enviar"}
          >
            <IconSend size={24} />
          </button>
        ) : (
          <button
            className="sc-input__btn sc-input__mic-btn"
            onClick={handleStartRecording}
            disabled={blocked}
            title="Gravar áudio"
          >
            <IconMicrophone size={24} />
          </button>
        )}
      </div>
    </div>
  );
});

ChatInput.displayName = "ChatInput";

export default ChatInput;
